import React, { useState } from "react";
import {
  Search, Building2, Store, ChevronDown, Check, ShieldCheck, LayoutDashboard, Users as UsersIcon,
  Receipt, SlidersHorizontal, ShieldAlert, Coins, TrendingUp, Boxes, RotateCcw, Truck, Mail, Plug,
  Eye, Pencil, X, FileSignature, FolderOpen,
} from "lucide-react";

/* A non-functional mock-up of two Ops Console layouts, so the shape can be picked
   before it is built for real. Nothing here talks to the API. */

const CUSTOMERS = [
  { id: "1", name: "MiamiSample.com", code: "BH-1042", stores: 2, open: 3 },
  { id: "2", name: "At War Worldwide", code: "BH-1088", stores: 1 },
  { id: "3", name: "Tiny Jams", code: "BH-1101", stores: 1 },
  { id: "4", name: "Palmetto Goods", code: "BH-1120", stores: 1, open: 1 },
  { id: "5", name: "Nova Supply Co.", code: "BH-1133", stores: 3 },
];

const GROUPS = [
  {
    g: "Customers", cards: [
      { l: "Customers", d: "Billing, contracts, claims & docs per brand", i: Building2, a: "blue" },
      { l: "Overview", d: "Pulse across your customer base", i: LayoutDashboard, a: "blue" },
      { l: "Accounts", d: "Company records & integrations", i: Building2, a: "slate", n: 12 },
      { l: "Users", d: "Approvals, roles & access", i: UsersIcon, a: "orange", n: 41 },
    ],
  },
  {
    g: "Money", cards: [
      { l: "Monthly Billing", d: "Run the month: review, credit, lock, invoice", i: Receipt, s: true },
      { l: "Rate Cards", d: "Set-once pricing per client", i: SlidersHorizontal, s: true },
      { l: "Credits & Claims", d: "What we credited back, by client", i: ShieldAlert, s: true },
      { l: "Our Costs", d: "What the work costs us — super admin", i: Coins, s: true },
      { l: "Profit Centre", d: "Markup & margin by client — super admin", i: TrendingUp, s: true },
    ],
  },
  {
    g: "Warehouse", cards: [
      { l: "Inventory", d: "Stock & receiving", i: Boxes, a: "green" },
      { l: "Returns", d: "Processed returns & digests", i: RotateCcw, a: "blue" },
      { l: "Claims", d: "Damage / loss claims", i: ShieldAlert, a: "red" },
    ],
  },
  {
    g: "Settings", cards: [
      { l: "Carriers", d: "Carrier names & services", i: Truck, a: "slate" },
      { l: "Emails", d: "Weekly business updates", i: Mail, a: "slate" },
      { l: "Integrations", d: "Stripe & ShipStation connection", i: Plug, a: "slate" },
    ],
  },
];

const ACCENT = {
  blue: "text-bh-blue bg-bh-blue/10", orange: "text-orange-600 bg-orange-100",
  red: "text-red-600 bg-red-100", green: "text-emerald-600 bg-emerald-100",
  slate: "text-slate-600 bg-slate-100",
};

const initials = (n = "") => n.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

export default function OpsLayoutPreview() {
  const [opt, setOpt] = useState("d");
  return (
    <div className="space-y-5" data-testid="ops-layout-preview">
      <div>
        <span className="inline-block text-xs uppercase tracking-wider font-semibold text-orange-600 bg-orange-100 px-2 py-0.5 rounded">Mock-up — nothing here is wired up</span>
        <h1 className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: "Manrope" }}>Ops Console layout options</h1>
        <p className="text-slate-500 mt-1">Pick the customer first, then pick what you're doing. Company-wide work lives on its own side.</p>
      </div>

      <div className="inline-flex bg-white border border-slate-200 rounded-xl p-1 gap-1 flex-wrap">
        {[["d", "D — Setup drawer"], ["e", "E — Two-block sidebar"], ["f", "F — One setup sheet"], ["c", "C — Customer first"], ["a", "A — Context bar"], ["b", "B — Customer rail"]].map(([v, l]) => (
          <button key={v} data-testid={`preview-opt-${v}`} onClick={() => setOpt(v)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${opt === v ? "bg-bh-navy-deep text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            {l}
          </button>
        ))}
      </div>

      {opt === "d" && <OptionD />}
      {opt === "e" && <OptionE />}
      {opt === "f" && <OptionF />}
      {opt === "c" && <OptionC />}
      {opt === "a" && <OptionA />}
      {opt === "b" && <OptionB />}
    </div>
  );
}

/* ================= shared: what "settings for a customer" means ================= */
const SETUP_TABS = [
  { l: "Account & stores", i: Store, d: "Link ShipStation stores, Stripe, addresses" },
  { l: "Rate card", i: SlidersHorizontal, d: "Markups, fulfillment tiers, prepayment", s: true },
  { l: "Contract", i: FileSignature, d: "Terms, dates, signed copy" },
  { l: "Documents", i: FolderOpen, d: "Anything you keep on file" },
  { l: "Users & access", i: UsersIcon, d: "Who can log in, and to what" },
  { l: "Emails", i: Mail, d: "Weekly update opt-in and recipients" },
];

const COMPANY_BOARD = [
  { g: "Across all customers", cards: [
    { l: "Overview", d: "Pulse across your customer base", i: LayoutDashboard, a: "blue" },
    { l: "Run the month", d: "Calculate, review and lock every client", i: Receipt, s: true },
    { l: "Profit by client", d: "Markup & margin league table", i: TrendingUp, s: true },
    { l: "Credits given", d: "What we credited back, by client", i: ShieldAlert, s: true },
  ] },
  { g: "Warehouse queues", cards: [
    { l: "Receiving & inventory", d: "Stock, POs and receiving", i: Boxes, a: "green" },
    { l: "Returns", d: "Processed returns & digests", i: RotateCcw, a: "blue" },
    { l: "Claims", d: "Damage / loss claims", i: ShieldAlert, a: "red" },
    { l: "Users & approvals", d: "Approvals, roles & access", i: UsersIcon, a: "orange", n: 41 },
  ] },
  { g: "Company settings", cards: [
    { l: "Our Costs", d: "What the work costs us", i: Coins, s: true },
    { l: "Carriers", d: "Carrier names & services", i: Truck, a: "slate" },
    { l: "Weekly emails", d: "Schedule & content", i: Mail, a: "slate" },
    { l: "Integrations", d: "Stripe & ShipStation connection", i: Plug, a: "slate" },
  ] },
];

function CompanyBoard({ cols = "xl:grid-cols-4", onCard }) {
  return (
    <div className="space-y-5">
      {COMPANY_BOARD.map((grp) => (
        <div key={grp.g}>
          <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-400 mb-2">{grp.g}</p>
          <div className={`grid grid-cols-2 md:grid-cols-3 ${cols} gap-3`}>
            {grp.cards.map((c) => (
              <button key={c.l} onClick={() => onCard?.(c.l)}
                className={`text-left p-4 rounded-xl border shadow-sm hover:shadow-md transition-all flex items-center gap-3 ${c.s ? "bg-red-50/40 border-red-200 hover:border-red-400" : "bg-white border-slate-200 hover:border-bh-blue"}`}>
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${c.s ? "text-red-600 bg-red-100" : ACCENT[c.a]}`}><c.i className="w-5 h-5" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="text-[15px] font-bold text-bh-navy-deep truncate" style={{ fontFamily: "Manrope" }}>{c.l}</p>
                    {c.n != null && <span className="text-xs font-bold text-slate-400 tabular-nums">{c.n}</span>}
                    {c.s && <ShieldCheck className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                  </div>
                  <p className="text-xs text-slate-500 truncate">{c.d}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function CustomerPickerBar({ onPick, label = "Set up a customer", hint = "Rate card, stores, contract, users — everything you configure per brand" }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-3 relative">
      <span className="w-10 h-10 rounded-lg bg-bh-navy-deep text-white flex items-center justify-center shrink-0"><Building2 className="w-5 h-5" /></span>
      <div className="min-w-0">
        <p className="text-[15px] font-bold text-bh-navy-deep" style={{ fontFamily: "Manrope" }}>{label}</p>
        <p className="text-xs text-slate-500">{hint}</p>
      </div>
      <div className="ml-auto relative">
        <button onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-2 bg-bh-navy-deep hover:opacity-90 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-opacity">
          <Search className="w-4 h-4" /> Choose a customer <ChevronDown className="w-4 h-4 opacity-70" />
        </button>
        {open && (
          <div className="absolute right-0 z-20 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-xl p-2">
            <div className="relative mb-2">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input autoFocus placeholder="Search customers…" className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
            </div>
            {CUSTOMERS.map((c) => (
              <button key={c.id} onClick={() => { setOpen(false); onPick(c); }}
                className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-50 text-left">
                <span className="w-8 h-8 rounded-full bg-slate-200 text-slate-600 text-[11px] font-bold flex items-center justify-center">{initials(c.name)}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-slate-900 truncate">{c.name}</span>
                  <span className="block text-[11px] text-slate-400 font-mono">{c.code} · {c.stores} store{c.stores === 1 ? "" : "s"}</span>
                </span>
              </button>
            ))}
            <div className="h-px bg-slate-100 my-1" />
            <button className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-slate-50 text-sm font-semibold text-bh-blue">+ New customer account</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= OPTION D — Everything is home, customer setup slides in ================= */
function OptionD() {
  const [cust, setCust] = useState(null);
  const [tab, setTab] = useState("Rate card");

  return (
    <div className="space-y-3" data-testid="preview-option-d">
      <Note>
        You land on <strong>Everything</strong> — the company-wide board, no customer to pick. When you need to set a
        customer up, choose them from the bar and their settings slide in over the board as a full-height panel
        (Account &amp; stores · Rate card · Contract · Documents · Users · Emails). Close it and you're back where you
        were. Day-to-day work (run the month, queues) never asks you to pick anyone.
      </Note>

      <div className="rounded-xl border border-slate-200 bg-bh-bg p-5 space-y-4 relative overflow-hidden min-h-[620px]">
        <CustomerPickerBar onPick={(c) => { setCust(c); setTab("Rate card"); }} />
        <CompanyBoard />

        {cust && (
          <>
            <div className="absolute inset-0 bg-bh-navy-deep/40 backdrop-blur-[2px]" onClick={() => setCust(null)} />
            <div className="absolute top-0 right-0 bottom-0 w-full sm:w-[560px] bg-white border-l border-slate-200 shadow-2xl flex flex-col">
              <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
                <span className="w-10 h-10 rounded-full bg-bh-orange text-bh-navy-deep text-xs font-bold flex items-center justify-center shrink-0">{initials(cust.name)}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-400">Customer setup</p>
                  <h3 className="text-lg font-bold text-slate-900 truncate" style={{ fontFamily: "Manrope" }}>{cust.name}</h3>
                  <p className="text-xs text-slate-400 font-mono">{cust.code} · {cust.stores} store{cust.stores === 1 ? "" : "s"}</p>
                </div>
                <button onClick={() => setCust(null)} className="text-slate-400 hover:text-slate-700 p-1"><X className="w-5 h-5" /></button>
              </div>
              <div className="px-3 py-2 border-b border-slate-100 flex flex-wrap gap-1">
                {SETUP_TABS.map((t) => {
                  const on = tab === t.l;
                  return (
                    <button key={t.l} onClick={() => setTab(t.l)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${on ? "bg-bh-blue text-white" : t.s ? "text-red-700 hover:bg-red-50" : "text-slate-600 hover:bg-slate-50"}`}>
                      <t.i className="w-3.5 h-3.5" /> {t.l}
                    </button>
                  );
                })}
              </div>
              <div className="flex-1 overflow-y-auto p-4 bg-bh-bg">
                {tab === "Account & stores" ? <StoresMock cust={cust} /> : <PanelMock title={tab} cust={cust} inline />}
              </div>
              <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-3">
                <button className="bg-bh-blue text-white text-sm font-semibold px-4 py-2 rounded-md">Save</button>
                <button className="text-xs font-semibold text-slate-500 hover:text-bh-blue inline-flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> View their portal</button>
                <span className="ml-auto text-xs text-slate-400">Their bill and profit live on the board behind</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ================= OPTION E — two-block sidebar ================= */
function OptionE() {
  const [cust, setCust] = useState(CUSTOMERS[0]);
  const [sel, setSel] = useState({ kind: "company", l: "Run the month" });

  return (
    <div className="space-y-3" data-testid="preview-option-e">
      <Note>
        No bars, no cards — one navigation list on the left, split into two blocks. <strong>Company</strong> (the
        default landing) is everything cross-client. <strong>Customer setup</strong> has the picker at its head; the
        six settings screens under it stay on whoever is picked. You can see where you are at all times, and the
        two kinds of work can't be confused.
      </Note>

      <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm flex flex-col lg:flex-row bg-white min-h-[620px]">
        <div className="lg:w-72 shrink-0 border-b lg:border-b-0 lg:border-r border-slate-200 bg-white p-3 space-y-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-400 px-2 mb-1.5">Company</p>
            {COMPANY_BOARD.flatMap((g) => g.cards).map((c) => {
              const on = sel.kind === "company" && sel.l === c.l;
              return (
                <button key={c.l} onClick={() => setSel({ kind: "company", l: c.l })}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left text-sm font-semibold transition-colors ${on ? "bg-bh-navy-deep text-white" : c.s ? "text-red-700 hover:bg-red-50" : "text-slate-600 hover:bg-slate-50"}`}>
                  <c.i className="w-4 h-4 shrink-0" /> <span className="truncate flex-1">{c.l}</span>
                  {c.s && !on && <ShieldCheck className="w-3 h-3 text-red-400" />}
                </button>
              );
            })}
          </div>

          <div className="pt-3 border-t border-slate-100">
            <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-400 px-2 mb-1.5">Customer setup</p>
            <select value={cust.id} onChange={(e) => setCust(CUSTOMERS.find((c) => c.id === e.target.value))}
              className="w-full mb-1.5 px-2.5 py-2 border border-slate-300 rounded-lg text-sm font-semibold bg-white">
              {CUSTOMERS.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {SETUP_TABS.map((t) => {
              const on = sel.kind === "customer" && sel.l === t.l;
              return (
                <button key={t.l} onClick={() => setSel({ kind: "customer", l: t.l })}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left text-sm font-semibold transition-colors ${on ? "bg-bh-blue text-white" : t.s ? "text-red-700 hover:bg-red-50" : "text-slate-600 hover:bg-slate-50"}`}>
                  <t.i className="w-4 h-4 shrink-0" /> <span className="truncate flex-1">{t.l}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 min-w-0 bg-bh-bg p-5">
          {sel.kind === "customer" ? (
            <>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 rounded-full bg-bh-orange text-bh-navy-deep text-xs font-bold flex items-center justify-center">{initials(cust.name)}</span>
                <div>
                  <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{cust.name} — {sel.l}</h2>
                  <p className="text-xs text-slate-400 font-mono">{cust.code} · {cust.stores} store{cust.stores === 1 ? "" : "s"}</p>
                </div>
              </div>
              {sel.l === "Account & stores" ? <StoresMock cust={cust} /> : <PanelMock title={sel.l} cust={cust} inline />}
            </>
          ) : (
            <>
              <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Manrope" }}>{sel.l} — all customers</h2>
              <PanelMock title={sel.l} cust={null} inline />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ================= OPTION F — one long setup sheet, no tabs ================= */
function OptionF() {
  const [cust, setCust] = useState(null);
  const [openSec, setOpenSec] = useState("Rate card");

  return (
    <div className="space-y-3" data-testid="preview-option-f">
      <Note>
        Same landing (<strong>Everything</strong>), but a customer's setup is <strong>one page, not tabs</strong> — a
        setup sheet with collapsible sections and a completeness checklist at the top ("Stores linked ✓ · Rate card ✓ ·
        Contract missing"). Good for onboarding a new brand in one pass; you never hunt for which tab holds a setting.
      </Note>

      <div className="rounded-xl border border-slate-200 bg-bh-bg p-5 space-y-4 min-h-[620px]">
        {!cust ? (
          <>
            <CustomerPickerBar onPick={(c) => setCust(c)} label="Open a customer's setup sheet" />
            <CompanyBoard />
          </>
        ) : (
          <>
            <button onClick={() => setCust(null)} className="text-sm font-semibold text-slate-500 hover:text-bh-blue">← Back to Everything</button>
            <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 flex flex-wrap items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-bh-orange text-bh-navy-deep text-xs font-bold flex items-center justify-center">{initials(cust.name)}</span>
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-slate-900 truncate" style={{ fontFamily: "Manrope" }}>{cust.name} — setup</h2>
                <p className="text-xs text-slate-400 font-mono">{cust.code} · {cust.stores} store{cust.stores === 1 ? "" : "s"}</p>
              </div>
              <div className="ml-auto flex flex-wrap gap-1.5">
                {[["Stores linked", true], ["Rate card", true], ["Contract", false], ["Users", true], ["Emails", false]].map(([l, ok]) => (
                  <span key={l} className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full ${ok ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                    {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} {l}
                  </span>
                ))}
              </div>
            </div>

            {SETUP_TABS.map((t) => {
              const on = openSec === t.l;
              return (
                <div key={t.l} className={`bg-white border rounded-xl ${t.s ? "border-red-200" : "border-slate-200"}`}>
                  <button onClick={() => setOpenSec(on ? null : t.l)} className="w-full flex items-center gap-3 px-5 py-3.5 text-left">
                    <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${t.s ? "bg-red-100 text-red-600" : "bg-slate-100 text-slate-600"}`}><t.i className="w-4 h-4" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[15px] font-bold text-bh-navy-deep" style={{ fontFamily: "Manrope" }}>{t.l}</span>
                      <span className="block text-xs text-slate-500">{t.d}</span>
                    </span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${on ? "rotate-180" : ""}`} />
                  </button>
                  {on && (
                    <div className="px-5 pb-5">
                      {t.l === "Account & stores" ? <StoresMock cust={cust} /> : <PanelMock title={t.l} cust={cust} inline />}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="flex items-center gap-3">
              <button className="bg-bh-blue text-white text-sm font-semibold px-5 py-2.5 rounded-md">Save all</button>
              <span className="text-xs text-slate-400">One Save for the whole sheet — no tab-by-tab saving.</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ================= OPTION C — customer first, company-wide separate ================= */
const CUSTOMER_TABS = [
  { g: "Their record", t: [
    { l: "Overview", i: LayoutDashboard }, { l: "Account & stores", i: Store }, { l: "Contract", i: FileSignature },
    { l: "Documents", i: FolderOpen }, { l: "Users", i: UsersIcon },
  ] },
  { g: "Their money", t: [
    { l: "Monthly bill", i: Receipt, s: true }, { l: "Rate card", i: SlidersHorizontal, s: true },
    { l: "Credits & claims", i: ShieldAlert, s: true }, { l: "Profit", i: TrendingUp, s: true },
  ] },
  { g: "Their warehouse", t: [
    { l: "Inventory", i: Boxes }, { l: "Returns", i: RotateCcw }, { l: "Emails", i: Mail },
  ] },
];

const COMPANY_GROUPS = [
  { g: "Across all customers", cards: [
    { l: "Overview", d: "Pulse across your customer base", i: LayoutDashboard, a: "blue" },
    { l: "Run the month", d: "Calculate, review and lock every client", i: Receipt, s: true },
    { l: "Profit by client", d: "Markup & margin league table — super admin", i: TrendingUp, s: true },
    { l: "Credits given", d: "What we credited back, by client", i: ShieldAlert, s: true },
  ] },
  { g: "Set up & admin", cards: [
    { l: "Accounts", d: "Create a customer, link their stores", i: Building2, a: "slate", n: 12 },
    { l: "Users & approvals", d: "Approvals, roles & access", i: UsersIcon, a: "orange", n: 41 },
    { l: "Our Costs", d: "What the work costs us — super admin", i: Coins, s: true },
    { l: "Carriers", d: "Carrier names & services", i: Truck, a: "slate" },
    { l: "Emails", d: "Weekly business updates", i: Mail, a: "slate" },
    { l: "Integrations", d: "Stripe & ShipStation connection", i: Plug, a: "slate" },
  ] },
  { g: "Warehouse queues", cards: [
    { l: "Receiving & inventory", d: "Stock, POs and receiving", i: Boxes, a: "green" },
    { l: "Returns", d: "Processed returns & digests", i: RotateCcw, a: "blue" },
    { l: "Claims", d: "Damage / loss claims", i: ShieldAlert, a: "red" },
  ] },
];

function OptionC() {
  const [mode, setMode] = useState("customer"); // customer | company
  const [cust, setCust] = useState(CUSTOMERS[0]);
  const [tab, setTab] = useState("Rate card");
  const [picking, setPicking] = useState(false);

  return (
    <div className="space-y-3" data-testid="preview-option-c">
      <Note>
        Two doors, never mixed. <strong>A customer</strong> — choose the brand once, then every tool you need for
        them sits in one row (including <em>Account &amp; stores</em>, which is where you add or edit their ShipStation
        stores, so there's no separate "Customers" card fighting the picker). <strong>Everything</strong> — the
        company-wide side: run the month for all clients, profit league table, carriers, integrations, new accounts,
        warehouse queues.
      </Note>

      <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
        {/* Mode switch + customer identity */}
        <div className="bg-bh-navy-deep px-3 sm:px-4 py-2.5 flex flex-wrap items-center gap-3">
          <div className="inline-flex bg-white/10 rounded-lg p-0.5">
            {[["customer", "A customer", Building2], ["company", "Everything", LayoutDashboard]].map(([v, l, I]) => (
              <button key={v} onClick={() => setMode(v)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${mode === v ? "bg-white text-bh-navy-deep" : "text-white/70 hover:text-white"}`}>
                <I className="w-3.5 h-3.5" /> {l}
              </button>
            ))}
          </div>

          {mode === "customer" ? (
            <div className="relative flex items-center gap-3 min-w-0">
              <span className="w-9 h-9 rounded-full bg-bh-orange text-bh-navy-deep text-xs font-bold flex items-center justify-center shrink-0">{initials(cust.name)}</span>
              <button onClick={() => setPicking((p) => !p)} className="min-w-0 text-left group">
                <span className="flex items-center gap-1.5">
                  <span className="text-base font-bold text-white truncate" style={{ fontFamily: "Manrope" }}>{cust.name}</span>
                  <ChevronDown className="w-4 h-4 text-white/60 group-hover:text-white" />
                </span>
                <span className="block text-[11px] text-white/50 font-mono">{cust.code} · {cust.stores} store{cust.stores === 1 ? "" : "s"}</span>
              </button>
              {picking && (
                <div className="absolute z-20 top-full left-0 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-xl p-2">
                  <div className="relative mb-2">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input autoFocus placeholder="Search customers…" className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
                  </div>
                  {CUSTOMERS.map((c) => (
                    <button key={c.id} onClick={() => { setCust(c); setPicking(false); }}
                      className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-50 text-left">
                      <span className="w-8 h-8 rounded-full bg-slate-200 text-slate-600 text-[11px] font-bold flex items-center justify-center">{initials(c.name)}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-slate-900 truncate">{c.name}</span>
                        <span className="block text-[11px] text-slate-400 font-mono">{c.code} · {c.stores} store{c.stores === 1 ? "" : "s"}</span>
                      </span>
                      {cust.id === c.id && <Check className="w-4 h-4 text-bh-blue" />}
                    </button>
                  ))}
                  <div className="h-px bg-slate-100 my-1" />
                  <button className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-slate-50 text-sm font-semibold text-bh-blue">+ New customer account</button>
                </div>
              )}
              <span className="ml-auto flex items-center gap-2 pl-2">
                <button className="text-xs font-semibold text-white/70 hover:text-white inline-flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> View as</button>
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-white/10 text-white flex items-center justify-center shrink-0"><Building2 className="w-4 h-4" /></span>
              <div>
                <p className="text-base font-bold text-white" style={{ fontFamily: "Manrope" }}>All customers</p>
                <p className="text-[11px] text-white/50">12 accounts · 16 stores · company-wide tools</p>
              </div>
            </div>
          )}
        </div>

        {mode === "customer" ? (
          <>
            {/* Per-customer tool row, grouped */}
            <div className="bg-white border-b border-slate-200 px-3 py-2.5 space-y-1.5">
              {CUSTOMER_TABS.map((grp) => (
                <div key={grp.g} className="flex flex-wrap items-center gap-1">
                  <span className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 w-[104px] shrink-0">{grp.g}</span>
                  {grp.t.map((t) => {
                    const on = tab === t.l;
                    return (
                      <button key={t.l} onClick={() => setTab(t.l)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                          on ? "bg-bh-blue text-white" : t.s ? "text-red-700 hover:bg-red-50" : "text-slate-600 hover:bg-slate-50"}`}>
                        <t.i className="w-3.5 h-3.5" /> {t.l}
                        {t.s && !on && <ShieldCheck className="w-3 h-3 text-red-400" />}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="bg-bh-bg p-5 min-h-[420px]">
              {tab === "Account & stores" ? <StoresMock cust={cust} /> : <PanelMock title={`${tab} — ${cust.name}`} cust={cust} inline />}
            </div>
          </>
        ) : (
          <div className="bg-bh-bg p-5 space-y-5 min-h-[520px]">
            {COMPANY_GROUPS.map((grp) => (
              <div key={grp.g}>
                <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-400 mb-2">{grp.g}</p>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                  {grp.cards.map((c) => (
                    <div key={c.l}
                      className={`text-left p-4 rounded-xl border shadow-sm flex items-center gap-3 ${c.s ? "bg-red-50/40 border-red-200" : "bg-white border-slate-200"}`}>
                      <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${c.s ? "text-red-600 bg-red-100" : ACCENT[c.a]}`}><c.i className="w-5 h-5" /></div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <p className="text-[15px] font-bold text-bh-navy-deep truncate" style={{ fontFamily: "Manrope" }}>{c.l}</p>
                          {c.n != null && <span className="text-xs font-bold text-slate-400 tabular-nums">{c.n}</span>}
                          {c.s && <ShieldCheck className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                        </div>
                        <p className="text-xs text-slate-500 truncate">{c.d}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <p className="text-xs text-slate-400">Nothing here asks you to pick a customer — these are the all-client and set-up screens.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function StoresMock({ cust }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Account &amp; stores — {cust.name}</h3>
          <p className="text-xs text-slate-500 mt-0.5">Where stores get added or swapped. Adding a store here doesn't change who you're working on.</p>
        </div>
        <button className="inline-flex items-center gap-1.5 bg-bh-blue text-white text-xs font-semibold px-3 py-2 rounded-md">+ Link a ShipStation store</button>
      </div>
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>{["ShipStation store", "Store ID", "Shipments (200d)", "Billed into", ""].map((h) => <th key={h} className="py-2 px-4 font-medium">{h}</th>)}</tr>
        </thead>
        <tbody>
          {[["MiamiSample.com", "412887", "8,190", "Combined bill"], ["Tiny Jams", "827543", "612", "Combined bill"]].map((r) => (
            <tr key={r[1]} className="border-t border-slate-100">
              <td className="py-2.5 px-4 font-semibold text-slate-800">{r[0]}</td>
              <td className="py-2.5 px-4 font-mono text-xs text-slate-500">{r[1]}</td>
              <td className="py-2.5 px-4 text-slate-600 tabular-nums">{r[2]}</td>
              <td className="py-2.5 px-4 text-slate-600">{r[3]}</td>
              <td className="py-2.5 px-4 text-right"><span className="text-xs font-semibold text-slate-400">Unlink</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ================= OPTION A — pinned dark context bar ================= */
function OptionA() {
  const [cust, setCust] = useState(null); // null = All customers
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState("home");

  return (
    <div className="space-y-3" data-testid="preview-option-a">
      <Note>
        A slim dark bar sits under the top bar and never moves. It holds one searchable customer picker
        (defaulting to <strong>All customers</strong>) plus its store count. The card board stays exactly as it is today —
        the only change is that every section you open is already scoped to the customer in that bar, and the
        repeated tab row inside billing is gone.
      </Note>

      <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
        {/* Context bar */}
        <div className="bg-bh-navy-deep px-4 h-[52px] flex items-center gap-3 relative">
          <Store className="w-4 h-4 text-bh-orange shrink-0" />
          <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-white/40 hidden sm:block">Working on</span>
          <div className="relative">
            <button onClick={() => setOpen((o) => !o)}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/15 border border-white/15 rounded-lg pl-3 pr-2 py-1.5 transition-colors">
              <span className="text-sm font-bold text-white">{cust ? cust.name : "All customers"}</span>
              <span className="text-[11px] font-semibold text-white/50 tabular-nums hidden sm:block">
                {cust ? `${cust.stores} store${cust.stores === 1 ? "" : "s"}` : "12 accounts · 16 stores"}
              </span>
              <ChevronDown className="w-4 h-4 text-white/60" />
            </button>
            {open && (
              <div className="absolute z-20 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-xl p-2">
                <div className="relative mb-2">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input autoFocus placeholder="Search customers…" className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
                </div>
                <button onClick={() => { setCust(null); setOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-50 text-left">
                  <span className="w-8 h-8 rounded-full bg-bh-navy-deep text-white flex items-center justify-center"><Building2 className="w-4 h-4" /></span>
                  <span className="flex-1 text-sm font-semibold text-slate-900">All customers</span>
                  {!cust && <Check className="w-4 h-4 text-bh-blue" />}
                </button>
                <div className="h-px bg-slate-100 my-1" />
                {CUSTOMERS.map((c) => (
                  <button key={c.id} onClick={() => { setCust(c); setOpen(false); }}
                    className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-50 text-left">
                    <span className="w-8 h-8 rounded-full bg-slate-200 text-slate-600 text-[11px] font-bold flex items-center justify-center">{initials(c.name)}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-slate-900 truncate">{c.name}</span>
                      <span className="block text-[11px] text-slate-400 font-mono">{c.code}</span>
                    </span>
                    {cust?.id === c.id && <Check className="w-4 h-4 text-bh-blue" />}
                  </button>
                ))}
              </div>
            )}
          </div>
          {cust && (
            <>
              <button className="text-xs font-semibold text-white/60 hover:text-white inline-flex items-center gap-1"><X className="w-3.5 h-3.5" /> Clear</button>
              <span className="ml-auto flex items-center gap-2">
                <button className="text-xs font-semibold text-white/70 hover:text-white inline-flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> View as</button>
                <button className="text-xs font-semibold text-white/70 hover:text-white inline-flex items-center gap-1.5"><Pencil className="w-3.5 h-3.5" /> Edit account</button>
              </span>
            </>
          )}
        </div>

        <div className="bg-bh-bg p-5 space-y-5 min-h-[520px]">
          {section === "home" ? (
            <>
              {GROUPS.map((grp) => (
                <div key={grp.g}>
                  <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-400 mb-2">{grp.g}</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
                    {grp.cards.map((c) => (
                      <button key={c.l} onClick={() => setSection(c.l)}
                        className={`text-left p-4 rounded-xl border shadow-sm hover:shadow-md transition-all flex items-center gap-3 ${c.s ? "bg-red-50/40 border-red-200 hover:border-red-400" : "bg-white border-slate-200 hover:border-bh-blue"}`}>
                        <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${c.s ? "text-red-600 bg-red-100" : ACCENT[c.a]}`}><c.i className="w-5 h-5" /></div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <p className="text-[15px] font-bold text-bh-navy-deep truncate" style={{ fontFamily: "Manrope" }}>{c.l}</p>
                            {c.n != null && <span className="text-xs font-bold text-slate-400 tabular-nums">{c.n}</span>}
                            {c.s && <ShieldCheck className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                          </div>
                          <p className="text-xs text-slate-500 truncate">{c.d}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <p className="text-xs text-slate-400">Cards are unchanged. What changes: whatever you open is already on {cust ? cust.name : "all customers"}.</p>
            </>
          ) : (
            <>
              <button onClick={() => setSection("home")} className="text-sm font-semibold text-slate-500 hover:text-bh-blue">← Back to console</button>
              <ScopeBanner cust={cust} />
              <PanelMock title={section} cust={cust} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ================= OPTION B — customer rail workspace ================= */
function OptionB() {
  const [cust, setCust] = useState(null);
  const [section, setSection] = useState("Rate Cards");
  const flat = GROUPS.flatMap((g) => g.cards.map((c) => ({ ...c, g: g.g })));

  return (
    <div className="space-y-3" data-testid="preview-option-b">
      <Note>
        The customer list becomes the console itself: a permanent rail on the left with <strong>All customers</strong> at the
        top, and one flat row of sections across the content area. Picking a customer on the left re-scopes every
        section instantly — no card board in the middle, one click to any tool.
      </Note>

      <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm flex flex-col lg:flex-row bg-white min-h-[572px]">
        {/* Rail */}
        <div className="lg:w-80 shrink-0 border-b lg:border-b-0 lg:border-r border-slate-200 flex flex-col bg-white">
          <div className="p-3 border-b border-slate-100">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input placeholder="Search customers…" className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
            </div>
          </div>
          <div className="p-2 space-y-1.5 overflow-y-auto">
            <button onClick={() => setCust(null)}
              className={`w-full text-left flex items-center gap-3 rounded-xl px-3 py-2.5 border transition-all ${!cust ? "border-bh-navy-deep bg-bh-navy-deep text-white" : "border-transparent hover:bg-slate-50"}`}>
              <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${!cust ? "bg-white/15 text-white" : "bg-slate-200 text-slate-600"}`}><Building2 className="w-4 h-4" /></span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold">All customers</span>
                <span className={`block text-[11px] ${!cust ? "text-white/60" : "text-slate-400"}`}>12 accounts · 16 stores</span>
              </span>
            </button>
            <div className="h-px bg-slate-100 my-1" />
            {CUSTOMERS.map((c) => {
              const on = cust?.id === c.id;
              return (
                <button key={c.id} onClick={() => setCust(c)}
                  className={`w-full text-left flex items-center gap-3 rounded-xl px-3 py-2.5 border transition-all ${on ? "border-blue-400 bg-blue-50/60 ring-1 ring-blue-200" : "border-transparent hover:bg-slate-50"}`}>
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${on ? "bg-bh-blue text-white" : "bg-slate-200 text-slate-600"}`}>{initials(c.name)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-slate-900 truncate">{c.name}</span>
                    <span className="flex items-center gap-2 mt-0.5">
                      <span className="text-[11px] text-slate-400 font-mono">{c.code}</span>
                      {c.open && <span className="text-[10px] font-semibold text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded-full">{c.open} open</span>}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Detail */}
        <div className="flex-1 min-w-0 bg-bh-bg">
          <div className="bg-white border-b border-slate-200 px-5 py-3.5 flex flex-wrap items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-bh-navy-deep text-white flex items-center justify-center text-xs font-bold shrink-0">
              {cust ? initials(cust.name) : <Building2 className="w-4 h-4" />}
            </span>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-slate-900 truncate" style={{ fontFamily: "Manrope" }}>{cust ? cust.name : "All customers"}</h2>
              <p className="text-xs text-slate-400 font-mono">{cust ? `${cust.code} · ${cust.stores} ShipStation store${cust.stores === 1 ? "" : "s"}` : "Everything below is aggregated"}</p>
            </div>
            {cust && (
              <span className="ml-auto flex items-center gap-2">
                <button className="text-xs font-semibold text-slate-500 hover:text-bh-blue inline-flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> View as</button>
                <button className="text-xs font-semibold text-slate-500 hover:text-bh-blue inline-flex items-center gap-1.5"><Pencil className="w-3.5 h-3.5" /> Edit</button>
              </span>
            )}
          </div>

          <div className="bg-white border-b border-slate-200 px-3 py-2 flex flex-wrap gap-1">
            {flat.map((c) => {
              const on = section === c.l;
              return (
                <button key={c.l} onClick={() => setSection(c.l)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    on ? "bg-bh-blue text-white" : c.s ? "text-red-700 hover:bg-red-50" : "text-slate-600 hover:bg-slate-50"}`}>
                  <c.i className="w-3.5 h-3.5" /> {c.l}
                  {c.s && !on && <ShieldCheck className="w-3 h-3 text-red-400" />}
                </button>
              );
            })}
          </div>

          <div className="p-5 space-y-4">
            <PanelMock title={section} cust={cust} inline />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= shared bits ================= */
function Note({ children }) {
  return (
    <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
      <LayoutDashboard className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
      <p className="text-sm text-blue-900 leading-relaxed">{children}</p>
    </div>
  );
}

function ScopeBanner({ cust }) {
  return (
    <div className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-lg px-3.5 py-2.5">
      <Store className="w-4 h-4 text-bh-orange shrink-0" />
      <p className="text-sm text-slate-600">
        Showing <strong className="text-slate-900">{cust ? cust.name : "all customers"}</strong>
        {cust ? " — change it in the bar at the top" : " — pick a customer at the top to narrow this down"}
      </p>
      <button className="ml-auto text-xs font-semibold text-bh-blue hover:underline">Change</button>
    </div>
  );
}

function PanelMock({ title, cust, inline = false }) {
  const rows = cust
    ? [["Tusk", "tusk", "Tusk Ground 939", "0%", "$0.00"], ["UPS", "ups_walleted", "UPS Ground Saver 85", "10%", "$0.35"], ["USPS", "stamps_com", "Ground Advantage 31", "10%", "$0.35"]]
    : [["MiamiSample.com", "2 stores", "8,190 shipments", "21.4%", "$30,381.81"], ["At War Worldwide", "1 store", "1,004 shipments", "10.0%", "$4,120.55"], ["Tiny Jams", "1 store", "612 shipments", "12.5%", "$2,884.10"]];
  const head = cust ? ["Carrier", "Code", "Services used", "Markup %", "Flat"] : ["Customer", "Stores", "Volume", "Effective markup", "This month"];
  return (
    <div className={`bg-white border border-slate-200 rounded-xl ${inline ? "" : "shadow-sm"}`}>
      <div className="px-5 py-4 border-b border-slate-100">
        <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{title}</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          {cust ? `Scoped to ${cust.name} — no store dropdown in here any more.` : "All customers — one row each, click a row to drop into that customer."}
        </p>
      </div>
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>{head.map((h) => <th key={h} className="py-2 px-4 font-medium">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r[0]} className="border-t border-slate-100">
              {r.map((cell, i) => <td key={`${head[i]}-${cell}`} className={`py-2.5 px-4 ${i === 0 ? "font-semibold text-slate-800" : "text-slate-600"}`}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
